import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { useParams } from 'react-router-dom';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import DriverInformationSection from './createRegistrationForm/DriverInformationSection';
import SupporterInformationSection from './createRegistrationForm/SupporterInformationSection';
import { addRegistration } from '../../../api/Registrations';
import { useEffect, useState } from 'react';
import Spinner from 'react-bootstrap/esm/Spinner';
import { getCompetition } from '../../../api/Competitions';
import { checkAgeEligibility } from '../../../utils/CheckEligibleRaceClasses';

function AddRegistrationModal(props) {
  const { setShowAddRegistrationSuccess, setShowAddRegistrationFail, ...rest } =
    props;
  const [updating, setUpdating] = useState(false);
  const { id: competitionId } = useParams();
  const [loadingCompetition, setLoadingCompetition] = useState(true);

  const [licenseClasses, setLicenseClasses] = useState([]);
  const [raceClasses, setRaceClasses] = useState([]);
  const [defaultLicenseClass, setDefaultLicenseClass] = useState('');
  const [defaultRaceClass, setDefaultRaceClass] = useState('');

  useEffect(() => {
    const fetchCompetition = async () => {
      const competitionData = await getCompetition(competitionId);

      const raceClasses =
        competitionData?.competition?.regulation?.raceClasses || [];
      const lowerCasedRaceClasses = raceClasses.map((cls) => cls.toLowerCase());
      const hasJunior = lowerCasedRaceClasses.includes('junior');
      const isFwdRwf =
        raceClasses.includes('fwd') || raceClasses.includes('rwd');

      const licenseClassesComputed = [
        ...['Junior'].filter(() => hasJunior),
        ...['Senior'].filter(
          () => !hasJunior || lowerCasedRaceClasses.length > 1
        ),
      ];

      const defaultLicenseClassComputed = licenseClassesComputed[0];
      let defaultRaceClassComputed =
        defaultLicenseClassComputed === 'Junior' ? 'Junior' : 'Senior';

      if (defaultRaceClassComputed === 'Senior' && isFwdRwf) {
        defaultRaceClassComputed = 'Fwd';
      }
      setLicenseClasses(licenseClassesComputed);
      setRaceClasses(raceClasses);
      setDefaultLicenseClass(defaultLicenseClassComputed);
      setDefaultRaceClass(defaultRaceClassComputed);
      setLoadingCompetition(false);
    };
    fetchCompetition();
  }, [competitionId]);

  const d = new Date();
  let fullYear = d.getFullYear();

  const registrationSchema = Yup.object().shape({
    firstName: Yup.string()
      .matches(/^[A-Öa-ö\- ]*$/, 'Endast bokstäver tillåtet')
      .min(2, 'För kort förnamn')
      .max(20, 'För långt förnamn')
      .required('Du måste ange ett förnamn'),
    lastName: Yup.string()
      .matches(/^[A-Öa-ö\- ]*$/, 'Endast bokstäver tillåtet')
      .min(2, 'För kort efternamn')
      .max(20, 'För långt efternamn')
      .required('Du måste ange ett efternamn'),
    birthDate: Yup.object().shape({
      year: Yup.string()
        .required('Du måste ange ett födelsedatum')
        .matches(/^[0-9]+$/, 'Endast siffror tillåtet')
        .min(4, 'Ange år med 4 siffror')
        .max(4, 'För många siffror')
        .test(
          'yearLimitMin',
          'Måste vara minst 15 år gammal för anmälan',
          (val) => +val < fullYear - 14
        )
        .test(
          'yearLimitMax',
          'Måste vara född tidigast 1920 för anmälan',
          (val) => +val >= 1920
        ),
      month: Yup.string()
        .required('Du måste ange en månad')
        .matches(/^[0-9]+$/, 'Endast siffror tillåtet')
        .max(2, 'För många siffror')
        .test(
          'monthLimit',
          'Månad måste vara vara mellan 1 och 12',
          (val) => +val <= 12 && +val > 0
        ),
      day: Yup.string()
        .required('Du måste ange en dag')
        .matches(/^[0-9]+$/, 'Endast siffror tillåtet')
        .max(2, 'För många siffror')
        .test(
          'dayLimit',
          'Dag måste vara mellan 1 och 31',
          (val) => +val <= 31 && +val > 0
        ),
    }),
    email: Yup.string()
      .email('Fel format på angiven e-post')
      .test(
        'check-availability',
        'Angiven e-post är redan registrerad',
        function (value) {
          for (let i = 0; i < props.registrations?.length; i++) {
            if (value === props.registrations[i]?.participantDetails?.email) {
              return false;
            }
          }
          return true;
        }
      )
      .required('Du måste ange en e-postadress'),
    phoneNumber: Yup.string()
      .matches(/^[0-9]+$/, 'Endast siffror tillåtet')
      .min(8, 'För få siffor i telefonnummer')
      .max(11, 'För många siffror i telefonnummer')
      .required('Du måste ange ett telefonnummer'),
    street: Yup.string()
      .min(2, 'För kort gatuadress')
      .required('Du måste ange en gatuadress'),
    zipCode: Yup.string()
      .matches(/^[0-9]+$/, 'Endast siffror tillåtet')
      .min(5, 'För få siffror i postnummer')
      .max(5, 'För många siffror i postnummer')
      .required('Du måste ange ett postnummer'),
    city: Yup.string()
      .matches(/^[A-Öa-ö\- ]*$/, 'Endast bokstäver tillåtet')
      .min(2, 'För kort postort')
      .required('Du måste ange en postort'),
    nationality: Yup.string().required('Du måste välja ett land'),
    lotsId: Yup.string()
      .matches(/^[0-9]+$/, 'Endast siffror tillåtet')
      .required('Du måste ange ett licensnummer'),
    raceClass: Yup.string()
      .when('birthDate', (birthDate) => {
        if (birthDate) {
          return Yup.string().test(
            'check-permission',
            'Angiven ålder är ej tillåten i vald tävlingsklass',
            function (value) {
              let birthYear = parseInt(birthDate.year);
              return checkAgeEligibility(birthYear, value);
            }
          );
        }
      })
      .required(),
    clubAffiliation: Yup.string().required('Du måste ange en klubb'),
    lotsId: Yup.string().required('Du måste ange ett licensnummer'),
    miscellaneous: Yup.string(),
    supporter: Yup.object().shape({
      name: Yup.string(),
      email: Yup.string(),
      phoneNumber: Yup.string(),
      street: Yup.string(),
      zipCode: Yup.string(),
      city: Yup.string(),
      miscellaneousInfo: Yup.string(),
    }),
  });

  if (loadingCompetition) {
    return (
      <Modal
        {...rest}
        centered
        aria-labelledby="contained-modal-title-vcenter"
        className="p-3"
      >
        <Modal.Header closeButton>
          <Modal.Title>Ny anmälan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Spinner animation="border" role="status" />
          <span> Laddar tävlingsdata...</span>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      {...rest}
      centered
      aria-labelledby="contained-modal-title-vcenter"
      className="p-3"
    >
      <Modal.Header closeButton>
        <Modal.Title>Ny anmälan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Lägg till en ny anmälan till tävlingen</p>

        <Formik
          initialValues={{
            firstName: '',
            lastName: '',
            birthDate: {
              year: '',
              month: '',
              day: '',
            },
            email: '',
            phoneNumber: '',
            street: '',
            zipCode: '',
            city: '',
            nationality: '',
            lotsId: '',
            licenseClass: defaultLicenseClass,
            raceClass: defaultRaceClass,
            clubAffiliation: '',
            lotsId: '',
            carBrand: '',
            miscellaneous: '',
            supporter: {
              licens: '',
              name: '',
              email: '',
              phoneNumber: '',
              street: '',
              zipCode: '',
              city: '',
            },
          }}
          onSubmit={async (values) => {
            setUpdating(true);
            const result = await addRegistration(competitionId, values);
            if (result) {
              switch (result.statusCode) {
                case 200:
                  setShowAddRegistrationSuccess(true);
                  break;
                default:
                  setShowAddRegistrationFail(true);
                  break;
              }
              props.onHide();
              setUpdating(false);
            }
          }}
          validationSchema={registrationSchema}
          enableReinitialize={true}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
            setFieldValue,
          }) => (
            <FormikForm
              id="form-add-registration"
              noValidate
              onSubmit={handleSubmit}
            >
              <DriverInformationSection
                values={values}
                handleChange={handleChange}
                handleBlur={handleBlur}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                licenseClasses={licenseClasses}
                raceClasses={raceClasses}
              />
              <SupporterInformationSection
                values={values}
                handleChange={handleChange}
                handleBlur={handleBlur}
                errors={errors}
                touched={touched}
              />
            </FormikForm>
          )}
        </Formik>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button onClick={() => props.onHide()} variant="outline-secondary">
          Avbryt
        </Button>
        <Button type="submit" form="form-add-registration">
          {updating ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              <span className="visually-hidden">Loading...</span>
              Lägg till anmälan
            </>
          ) : (
            'Lägg till anmälan'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddRegistrationModal;
